import { useState } from 'react';
import type { AnalysisResults } from '../../services/api/novel-analysis';

type ParseFn = (str: string | null) => unknown;

export function SummaryTab({ chapters, parseJson }: { chapters: AnalysisResults['chapters']; parseJson: ParseFn }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (idx: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  return (
    <div className="divide-y">
      {chapters.map((ch) => (
        <div key={ch.id} className="p-4">
          <button onClick={() => toggle(ch.chapter_index)} className="w-full text-left flex justify-between items-center">
            <span className="font-medium">第 {ch.chapter_index} 章</span>
            <span className="text-gray-400 text-sm">{expanded.has(ch.chapter_index) ? '收起' : '展開'}</span>
          </button>
          {expanded.has(ch.chapter_index) && (
            <div className="mt-3 space-y-3">
              {ch.summary && <p className="text-gray-700 text-sm">{ch.summary}</p>}

              {ch.overview && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">劇情概述</h4>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{ch.overview}</p>
                </div>
              )}

              {ch.key_events && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">關鍵事件</h4>
                  {(parseJson(ch.key_events) as Array<{ description: string; characters: string[]; importance: number }> || []).map((e, i) => (
                    <div key={i} className="text-sm text-gray-600 pl-3 border-l-2 border-gray-200 mb-1">
                      <span className="text-yellow-600">[{e.importance}/5]</span> {e.description}
                      {e.characters?.length > 0 && <span className="text-gray-400"> ({e.characters.join(', ')})</span>}
                    </div>
                  ))}
                </div>
              )}

              {ch.locations && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">地點</h4>
                  <div className="flex gap-1 flex-wrap">
                    {(parseJson(ch.locations) as string[] || []).map((loc, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">{loc}</span>
                    ))}
                  </div>
                </div>
              )}

              {ch.themes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">主題</h4>
                  <div className="flex gap-1 flex-wrap">
                    {(parseJson(ch.themes) as string[] || []).map((t, i) => (
                      <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              <TimelineMarkers data={parseJson(ch.timeline_markers)} />

              {ch.unresolved_threads && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">未解決線索</h4>
                  {(parseJson(ch.unresolved_threads) as Array<{ thread: string; status?: string }> || []).map((t, i) => (
                    <div key={i} className="text-sm text-gray-600 pl-3 border-l-2 border-yellow-300 mb-1">
                      {typeof t === 'string' ? t : t.thread}
                      {typeof t !== 'string' && t.status && <span className="text-gray-400 ml-1">({t.status})</span>}
                    </div>
                  ))}
                </div>
              )}

              <PlotTurningPoints data={parseJson(ch.plot_turning_points)} />
              <CharacterDevelopment data={parseJson(ch.character_development)} />
              <PlotSequence data={parseJson(ch.plot_sequence)} />
              <SexualRecords data={parseJson(ch.sexual_records)} />
            </div>
          )}
        </div>
      ))}
      {chapters.length === 0 && <p className="p-4 text-gray-500 text-sm">尚無摘要資料</p>}
    </div>
  );
}

function TimelineMarkers({ data }: { data: unknown }) {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  const markers = data as Array<{ time?: string; event?: string; position?: number; special_markers?: string[] }>;
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-600 mb-2">時間線</h4>
      <div className="relative">
        <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-200" />
        {markers.map((m, i) => (
          <div key={i} className="relative pl-6 pb-2">
            <div className="absolute left-0.5 top-1 w-3 h-3 rounded-full bg-blue-400 border-2 border-white" />
            <div className="text-sm">
              {m.time && <span className="text-blue-600 font-medium mr-2">{m.time}</span>}
              <span className="text-gray-700">{m.event}</span>
              {m.special_markers?.map((sm, j) => (
                <span key={j} className="ml-1 px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded text-xs">{sm}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlotTurningPoints({ data }: { data: unknown }) {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  const points = data as Array<{ title?: string; description?: string; significance?: string; sequence?: number }>;
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-600 mb-1">劇情轉折點</h4>
      {points.map((p, i) => (
        <div key={i} className="text-sm pl-3 border-l-2 border-red-200 mb-2">
          <div className="font-medium text-gray-700">
            {p.sequence != null && <span className="text-red-500 mr-1">#{p.sequence}</span>}
            {p.title}
          </div>
          {p.description && <p className="text-gray-600">{p.description}</p>}
          {p.significance && <p className="text-gray-400 text-xs mt-0.5">意義：{p.significance}</p>}
        </div>
      ))}
    </div>
  );
}

function CharacterDevelopment({ data }: { data: unknown }) {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  const devs = data as Array<{ character?: string; initial?: string; transformation?: string; final?: string }>;
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-600 mb-1">角色發展</h4>
      {devs.map((d, i) => (
        <div key={i} className="text-sm bg-gray-50 rounded p-2 mb-1">
          {d.character && <span className="font-medium text-gray-700">{d.character}：</span>}
          <div className="flex items-center gap-1 text-gray-600 flex-wrap mt-0.5">
            {d.initial && <span className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">{d.initial}</span>}
            {d.transformation && <><span className="text-gray-400">→</span><span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{d.transformation}</span></>}
            {d.final && <><span className="text-gray-400">→</span><span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">{d.final}</span></>}
          </div>
        </div>
      ))}
    </div>
  );
}

function PlotSequence({ data }: { data: unknown }) {
  if (!data || typeof data !== 'object') return null;
  const seq = data as Record<string, string>;
  const stages = ['setup', 'rising_action', 'climax', 'resolution'];
  const labels: Record<string, string> = { setup: '鋪陳', rising_action: '發展', climax: '高潮', resolution: '收束' };
  const colors: Record<string, string> = {
    setup: 'bg-gray-100 text-gray-700',
    rising_action: 'bg-blue-50 text-blue-700',
    climax: 'bg-red-50 text-red-700',
    resolution: 'bg-green-50 text-green-700',
  };
  const hasContent = stages.some((s) => seq[s]);
  if (!hasContent) return null;

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-600 mb-1">劇情結構</h4>
      <div className="grid grid-cols-2 gap-2">
        {stages.map((s) => seq[s] ? (
          <div key={s} className={`rounded p-2 text-sm ${colors[s]}`}>
            <span className="font-medium text-xs">{labels[s]}</span>
            <p className="mt-0.5">{seq[s]}</p>
          </div>
        ) : null)}
      </div>
    </div>
  );
}

function SexualRecords({ data }: { data: unknown }) {
  if (!data || typeof data !== 'object') return null;
  const records = data as { orgasm_records?: Array<{ character?: string; count?: number; description?: string }>; cumshot_records?: Array<{ character?: string; description?: string }> };
  if (!records.orgasm_records?.length && !records.cumshot_records?.length) return null;

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-600 mb-1">性愛記錄摘要</h4>
      {records.orgasm_records?.map((r, i) => (
        <div key={`o-${i}`} className="text-sm text-gray-600 pl-3 border-l-2 border-pink-200 mb-1">
          <span className="text-pink-600">高潮</span> {r.character}{r.count != null && ` ×${r.count}`}
          {r.description && <span className="text-gray-400"> — {r.description}</span>}
        </div>
      ))}
      {records.cumshot_records?.map((r, i) => (
        <div key={`c-${i}`} className="text-sm text-gray-600 pl-3 border-l-2 border-purple-200 mb-1">
          <span className="text-purple-600">射精</span> {r.character}
          {r.description && <span className="text-gray-400"> — {r.description}</span>}
        </div>
      ))}
    </div>
  );
}
